import { useEffect, useRef } from 'react';

/**
 * Flags unread messages in the browser tab title while the visitor is on
 * another tab.
 *
 * Someone who triggers a message and then switches tabs never sees the widget —
 * but the tab is still in their tab strip. Rewriting the title to
 * `(1) New message · …` pulls them back. It only ever shows while the tab is
 * hidden, so it never interrupts someone actively reading, and the original
 * title is always restored.
 */
export function useTabBadge(unreadCount: number): void {
  const originalTitle = useRef<string | null>(null);
  const applied = useRef(false);

  useEffect(() => {
    const restore = (): void => {
      if (applied.current && originalTitle.current !== null) {
        document.title = originalTitle.current;
      }
      applied.current = false;
      originalTitle.current = null;
    };

    const sync = (): void => {
      const shouldBadge = document.hidden && unreadCount > 0;

      if (shouldBadge) {
        // Capture the page's own title the first time we override it, so a
        // host SPA that changed its title still gets that value back.
        if (!applied.current) {
          originalTitle.current = document.title;
          applied.current = true;
        }
        const noun = unreadCount === 1 ? 'New message' : `${unreadCount} new messages`;
        document.title = `(${unreadCount}) ${noun} · ${originalTitle.current}`;
      } else {
        restore();
      }
    };

    sync();
    document.addEventListener('visibilitychange', sync);

    return () => {
      document.removeEventListener('visibilitychange', sync);
      restore();
    };
  }, [unreadCount]);
}
